import { Link, useSearch } from "@tanstack/react-location"
import moreJourneysLine from "assets/images/moreJourneysLine.svg"
import { useGraphqlQuery } from "graphql-mock"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import type { Journey } from "../../customApi"
import { getCenteredSlice } from "../../tools/getCenteredSlice"
import { useJourneyNavigation } from "../../tools/useJourneyNavigation"
import { useCurrentUserContext } from "../AppShell/CurrentUserProvider"
import { JourneyThumbnail } from "../JourneyThumbnail"
import { query } from "./gql"
import { YourJourneysListError } from "./YourJourneysListError"
import { YourJourneysListSkeleton } from "./YourJourneysListSkeleton"

import s from "./YourJourneysList.module.scss"

type YourJourneysListConnectedProps = {
  size: number
  onJourneyChange?: () => void
}
export const YourJourneysListConnected = ({
  size,
  onJourneyChange,
}: YourJourneysListConnectedProps) => {
  const { journeyIdParam, navigateToJourney } = useJourneyNavigation()
  const { data, error, isPending } = useGraphqlQuery(query, {
    page: 1,
    limit: 1000, // Big enough number to ensure we get all of 'em
  })

  const journeys = data?.productCategories.nodes ?? []
  const firstIncompleteJourneyId = data?.nextJourney?.id

  return (
    <YourJourneysList
      isLoading={isPending}
      error={error}
      activeJourneyId={journeyIdParam}
      firstIncompleteJourneyId={firstIncompleteJourneyId}
      journeys={journeys}
      onJourneyChange={(journeyId: string) => {
        if (onJourneyChange) onJourneyChange()
        navigateToJourney(journeyId)
      }}
      size={size}
    />
  )
}

type YourJourneysListProps = {
  isLoading: boolean
  error: unknown
  activeJourneyId?: string
  firstIncompleteJourneyId?: string
  journeys: Journey[]
  size: number
  onJourneyChange: (journeyId: string) => void
}
export const YourJourneysList = ({
  isLoading,
  error,
  activeJourneyId,
  firstIncompleteJourneyId,
  journeys,
  size,
  onJourneyChange,
}: YourJourneysListProps) => {
  const {
    currentUser: { avatar },
  } = useCurrentUserContext()

  const activeJourneyIndex = journeys.findIndex(
    (journey) => journey.id === activeJourneyId
  )

  const {
    items: centeredJourneys,
    itemsBefore,
    itemsAfter,
  } = getCenteredSlice(journeys, activeJourneyIndex, size)

  const getConnectorType = (journey: Journey, index: number): ConnectorType => {
    const isFirst = index === 0

    const isPrevJourneyCompleted =
      centeredJourneys[index - 1]?.progress?.certified ?? false
    const isTwoCompletedJourneysInARow =
      journey.progress.certified && isPrevJourneyCompleted

    if (isFirst && itemsBefore.length > 0) return "out"

    if (isFirst) return null

    if (isTwoCompletedJourneysInARow) return "linked"

    return "default"
  }

  const getJourneyItemVariant = (journey: Journey) => {
    const {
      isLocked,
      progress: { certified: isCertified },
    } = journey

    // A completed or locked journey always shows its icon, even if it happens to
    // be the "next" journey (see getJourneyItemThumbnail).
    if (isLocked) return "locked"
    if (isCertified) return "certified"
    return "default"
  }

  const getJourneyItemThumbnail = (journey: Journey) => {
    const {
      id,
      isLocked,
      backgroundImageUrl,
      teaserImageUrl,
      progress: { certified: isCertified },
    } = journey
    // Only mark the next journey with the user's avatar when it is genuinely not
    // completed/locked, otherwise a completed journey would show the avatar
    // instead of the checkmark.
    const showUserAvatar =
      firstIncompleteJourneyId === id && !isCertified && !isLocked
    const journeyThumbnail = showUserAvatar
      ? avatar
      : (teaserImageUrl ?? backgroundImageUrl)

    return journeyThumbnail || null
  }

  const hasMoreJourneys = itemsBefore.length > 0 || itemsAfter.length > 0

  const getContent = () => {
    if (isLoading) return <YourJourneysListSkeleton />

    if (error) return <YourJourneysListError error={error} />

    return (
      <>
        {centeredJourneys.map((journey: Journey, index: number) => (
          <JourneyListItem
            key={journey.id}
            journey={journey}
            connectorType={getConnectorType(journey, index)}
            isActive={journey.id === activeJourneyId}
            thumbnail={getJourneyItemThumbnail(journey)}
            variant={getJourneyItemVariant(journey)}
            onClick={() => onJourneyChange(journey.id)}
          />
        ))}

        {hasMoreJourneys && (
          <ShowAllButton shouldShowLine={itemsAfter.length > 0} />
        )}
      </>
    )
  }

  return <div className={s.journeyList}>{getContent()}</div>
}

type JourneyListItemProps = {
  journey: Journey
  connectorType: ConnectorType | null
  isActive: boolean
  thumbnail: string | null
  variant: "default" | "locked" | "certified"
  onClick: () => void
}
const JourneyListItem = ({
  journey,
  connectorType,
  isActive,
  thumbnail,
  variant,
  onClick,
}: JourneyListItemProps) => {
  const {
    isLocked,
    title,
    progress: { certified: isCertified },
  } = journey

  return (
    <div className={s.journeyItem}>
      {connectorType && <JourneyConnector type={connectorType} />}

      <button
        type="button"
        className={modifiers(s, "journeyItem__button", {
          isActive,
        })}
        onClick={onClick}
        title={title ?? ""}
      >
        <JourneyThumbnail thumbnail={thumbnail} variant={variant} />

        <div
          className={modifiers(s, "journeyItem__title", {
            isActive,
            isCertified,
            isLocked,
          })}
        >
          {title}
        </div>
      </button>
    </div>
  )
}

type ConnectorType = "linked" | "out" | "default" | null
const JourneyConnector = ({ type }: { type: ConnectorType }) => {
  return (
    <div
      className={modifiers(s, "journeyItem__connector", {
        isLinked: type === "linked",
        isOut: type === "out",
      })}
    />
  )
}

const ShowAllButton = ({ shouldShowLine }: { shouldShowLine: boolean }) => {
  const { t } = useTranslation()
  const searchParams = useSearch()

  return (
    <div className={s.showAll__wrapper}>
      <img
        className={modifiers(s, "showAll__image", { shouldShowLine })}
        src={moreJourneysLine}
        alt=""
      />
      <Link to="list" search={searchParams} className={s.showAll__button}>
        {t("journey.SHOW_ALL")}
      </Link>
    </div>
  )
}
